import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { DocumentsUploadExcel } from "components";
import { useUtilityBillById } from "hooks";
import { getFromLocalStorage, setToLocalStorage } from "utils";

import { UTILITY_BILL_PAGE } from "pages/authenticated/documents/document-summary-v2/route-consts";

import {
  PdfDocument,
  PdfEmptyData,
  PdfRender,
  usePdfPreviewLayout,
} from "./components";
import { FOOTER_HEIGHT } from "./consts";
import { BboxItem, CommonPdfPreviewProps, PageDimension } from "./types";
import "./css/style.css";

export const PdfPreview: FunctionComponent<CommonPdfPreviewProps> = ({
  downloadUrl,
  scale,
  billId,
  pageNumber,
  scrollVersion,
  activeBboxKey,
  onLoadSuccess,
}) => {
  const { containerWidth } = usePdfPreviewLayout({
    footerHeight: FOOTER_HEIGHT,
    debounceMs: 150,
  });

  const [isExcelFile, setIsExcelFile] = useState(false);
  const location = useLocation();

  const { billId: routeBillId } = useParams<{
    [UTILITY_BILL_PAGE.KEY]: string;
  }>();
  const activeBillId = billId || routeBillId || "";
  const { data: utilityBill } = useUtilityBillById(activeBillId);

  useEffect(() => {
    if (!downloadUrl) return;
    const urlWithoutQuery = downloadUrl.split("?")[0];
    const fileExtension = urlWithoutQuery
      .split("/")
      .pop()
      ?.split(".")
      .pop()
      ?.toLowerCase();
    const lastPathSegment = location.pathname.split("/").pop();

    setIsExcelFile(
      fileExtension !== "pdf" &&
        lastPathSegment !== "comments" &&
        lastPathSegment !== "Data_Entries",
    );
  }, [downloadUrl, location.pathname]);

  useEffect(() => {
    if (!utilityBill) return;
    const dataEntries = getFromLocalStorage("dataEntries");
    const dataArray = dataEntries && JSON.parse(dataEntries);
    if (dataArray && Object.keys(dataArray).length > 0) {
      dataArray.id = utilityBill.documentGuid;
      if (dataArray.original) {
        dataArray.original.id = utilityBill.documentGuid;
        dataArray.fileName = utilityBill.filename;
        dataArray.original.fileName = utilityBill.filename;
      }
      setToLocalStorage("dataEntries", JSON.stringify(dataArray));
    }
  }, [utilityBill]);

  const bboxesFromOpts = useMemo<Record<string, BboxItem>>(() => {
    try {
      const opts =
        utilityBill?.opts != null ? JSON.parse(utilityBill.opts as any) : null;
      return opts?.bboxes ?? {};
    } catch {
      return {};
    }
  }, [utilityBill?.opts]);

  const bboxes = useMemo<Record<string, BboxItem>>(() => {
    try {
      return utilityBill?.bboxes != null ? JSON.parse(utilityBill.bboxes) : {};
    } catch {
      return {};
    }
  }, [utilityBill?.bboxes]);

  const dimensions = useMemo<PageDimension[]>(() => {
    try {
      return utilityBill?.dimensions != null
        ? JSON.parse(utilityBill.dimensions).dimensions ?? []
        : [];
    } catch {
      return [];
    }
  }, [utilityBill?.dimensions]);

  const mergedBboxes = useMemo<Record<string, BboxItem>>(
    () => ({ ...bboxes, ...bboxesFromOpts }),
    [bboxes, bboxesFromOpts],
  );

  // Stabilize the URL passed to PdfDocument: only update when the base path changes
  // (i.e. a genuinely different document). Same base path = same document with a
  // refreshed auth token — PDF.js already has it loaded, so we keep the old URL to
  // avoid destroying and recreating the worker unnecessarily.
  const stableUrlRef = useRef(downloadUrl);
  const stableBaseRef = useRef(downloadUrl?.split("?")[0]);
  const currentBase = downloadUrl?.split("?")[0];
  if (currentBase !== stableBaseRef.current) {
    stableBaseRef.current = currentBase;
    stableUrlRef.current = downloadUrl;
  }
  const stableUrl = stableUrlRef.current;

  if (isExcelFile && billId) {
    return (
      <PdfRender>
        <DocumentsUploadExcel billIdExcelId={billId} />
      </PdfRender>
    );
  }

  return (
    <PdfRender downloadUrl={downloadUrl}>
      {!downloadUrl ? (
        <PdfEmptyData />
      ) : (
        <PdfDocument
          key={stableUrl}
          width={containerWidth}
          scale={scale}
          file={stableUrl as string}
          bboxes={mergedBboxes}
          dimensions={dimensions}
          onLoadSuccess={onLoadSuccess}
          pageNumber={pageNumber}
          scrollVersion={scrollVersion}
          activeBboxKey={activeBboxKey}
        />
      )}
    </PdfRender>
  );
};
